import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { ShoppingCart, ChevronDown, User, Package, Gift, Settings, LogOut } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Link, useNavigate, useLocation } from "react-router-dom"
import LoginDialog from "@/components/LoginDialog"
import SignupDialog from "@/components/SignupDialog"
import MobileAppBanner from "@/components/MobileAppBanner"

export default function Navigation() {
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const [isSignupOpen, setIsSignupOpen] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [currentUserName, setCurrentUserName] = useState<string | null>(null)
  const [isRoot, setIsRoot] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const handleSectionNavigation = (sectionId: string) => {
    if (location.pathname === '/') {
      // Si on est déjà sur la home, juste scroller
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      // Naviguer vers la home puis scroller
      navigate('/')
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }
  }

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      // Si on est déjà sur la home, scroll vers le top
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      // Naviguer vers la home puis scroll vers le top
      navigate('/')
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  }

  useEffect(() => {
    // Vérifier si l'élément #logged existe dans la page
    const checkLoggedStatus = () => {


      const appData = document.getElementById('app-data');
      if (appData) {
        const logged = appData.getAttribute('logged');
        const userName = appData.getAttribute('current_user_name');
        const root = appData.getAttribute('is_root');
        setIsLoggedIn(!!logged)
        setCurrentUserName(userName)
        setIsRoot(root === 'true')
      } else {
        setIsLoggedIn(false)
        setCurrentUserName(null)
        setIsRoot(false)
      }
    }

    // Vérifier au montage
    checkLoggedStatus()

    // Observer les changements dans le DOM
    const observer = new MutationObserver(checkLoggedStatus)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-border/50 rounded-b-3xl">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button onClick={handleLogoClick} className="flex items-center space-x-2">
              <img 
                src="/images/logo_square.png" 
                alt="Wememberz" 
                className="h-8 w-8 cursor-pointer md:hidden"
              />
              <img 
                src="/images/logo.png" 
                alt="Wememberz" 
                className="h-8 w-auto cursor-pointer hidden md:block"
              />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => handleSectionNavigation('albums')}
                className="text-foreground/80 hover:text-[#f9bb72] transition-smooth font-medium px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                Albums Photo
              </button>
              
              <button 
                onClick={() => handleSectionNavigation('application')}
                className="text-foreground/80 hover:text-[#f9bb72] transition-smooth font-medium px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                Concept
              </button>

              <button 
                onClick={() => handleSectionNavigation('occasions')}
                className="text-foreground/80 hover:text-[#f9bb72] transition-smooth font-medium px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                Occasions
              </button>
              <Link 
                to="/blog" 
                className="text-foreground/80 hover:text-[#f9bb72] transition-smooth font-medium px-3 py-2 rounded-lg hover:bg-accent/50"
              >
                Blog
              </Link>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              {isLoggedIn ? (
                <>
                  {/* Menu utilisateur connecté */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="font-medium hover:bg-accent/80 flex items-center space-x-2"
                      >
                        <User className="w-4 h-4" />
                        <span className="hidden md:inline">{currentUserName || "Mon profil"}</span>
                        <ChevronDown className="w-4 h-4 hidden md:inline" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <a href="/profiles/new" className="flex items-center cursor-pointer">
                          <User className="mr-2 h-4 w-4" />
                          <span>Mon compte</span>
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href="/profiles/commandes" className="flex items-center cursor-pointer">
                          <Package className="mr-2 h-4 w-4" />
                          <span>Mes commandes</span>
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href="/profiles/credit" className="flex items-center cursor-pointer">
                          <Gift className="mr-2 h-4 w-4" />
                          <span>Mes codes promo</span>
                        </a>
                      </DropdownMenuItem>
                      {isRoot && (
                        <DropdownMenuItem asChild>
                          <a href="/backoffice/users" className="flex items-center cursor-pointer">
                            <Settings className="mr-2 h-4 w-4" />
                            <span>Backoffice</span>
                          </a>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>
                        <form action="/users/sign_out" method="POST" className="w-full">
                          <input type="hidden" name="_method" value="delete" />
                          <button type="submit" className="flex items-center w-full text-left">
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>Déconnexion</span>
                          </button>
                        </form>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Bouton Albums Photos */}
                  <Button 
                    variant="rainbow" 
                    size="sm" 
                    className="font-medium shadow-lg"
                    asChild
                  >
                    <a href="/social_networks">Mes créations</a>
                  </Button>
                  
                  {/* Icône panier */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    asChild
                    className="font-medium hover:bg-accent/80"
                  >
                    <a href="/carts">
                      <ShoppingCart className="w-4 h-4" />
                    </a>
                  </Button>
                </>
              ) : (
                <>
                  {/* Boutons pour utilisateur non connecté */}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setIsLoginOpen(true)}
                    className="font-medium hover:bg-accent/80"
                  >
                    Se connecter
                  </Button>
                  <Button 
                    variant="rainbow" 
                    size="sm" 
                    className="font-medium shadow-lg"
                    onClick={() => setIsSignupOpen(true)}
                  >
                    S'inscrire
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <MobileAppBanner />

      <LoginDialog
        open={isLoginOpen} 
        onOpenChange={setIsLoginOpen} 
      />
      
      <SignupDialog 
        open={isSignupOpen} 
        onOpenChange={setIsSignupOpen} 
      />
    </>
  )
}